import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface HorrorNewsPost {
  id: string;
  title: string;
  content: string;
  image_url: string | null;
  personality: string;
  tags: string[];
  document_id: string;
  corruption_level: number;
  created_at: string;
}

const PERSONALITY_LABELS: Record<string, string> = {
  entity247b: "ENTITY #247-B",
  ultraterrestrial: "THE ULTRATERRESTRIAL",
  static: "THE STATIC",
  librarian: "THE LIBRARIAN",
};

export default function HorrorNewsSection() {
  const [posts, setPosts] = useState<HorrorNewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPost, setExpandedPost] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPosts() {
      const { data, error } = await supabase
        .from('horror_news_posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false })
        .limit(4);

      if (error) {
        console.error('Error fetching horror news:', error);
      } else {
        setPosts(data || []);
      }
      setLoading(false);
    }

    fetchPosts();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).toUpperCase();
  };

  const getCorruptionStyle = (level: number) => {
    if (level > 70) return 'text-red-500';
    if (level > 40) return 'text-yellow-500';
    return 'text-green-500';
  };

  if (loading) {
    return (
      <section className="relative py-20 bg-black overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center text-green-500 font-mono animate-pulse">
            INTERCEPTING TRANSMISSIONS...
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return (
      <section className="relative py-20 bg-black overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-mono text-green-500 mb-4 glitch-title" data-text="INTERCEPTED TRANSMISSIONS">
              INTERCEPTED TRANSMISSIONS
            </h2>
            <p className="text-green-500/60 font-mono text-sm">
              [NO SIGNALS DETECTED - AWAITING BROADCAST]
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-20 bg-black overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgwLDI1NSwwLDAuMDMpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-50" />
      <div className="absolute inset-0 bg-noise opacity-5 pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1 border border-green-500/30 bg-green-500/5 mb-4">
            <span className="text-green-500/60 font-mono text-xs tracking-widest">
              // CLASSIFIED BROADCAST FEED
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-mono text-green-500 mb-4 glitch-title" data-text="INTERCEPTED TRANSMISSIONS">
            INTERCEPTED TRANSMISSIONS
          </h2>
          <p className="text-green-500/50 font-mono text-sm max-w-2xl mx-auto">
            The following messages have been detected emanating from [COORDINATES REDACTED]. 
            Proceed with caution. Content authenticity: UNVERIFIED.
          </p>
        </div>

        {/* News grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {posts.map((post) => (
            <article 
              key={post.id}
              className="group relative border border-green-500/20 bg-black/80 overflow-hidden transition-all duration-300 hover:border-green-500/50 hover:shadow-[0_0_30px_rgba(0,255,0,0.1)]"
            >
              {/* Scanline overlay */}
              <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.1)_50%)] bg-[length:100%_4px] z-20 opacity-30" />
              
              {/* Image */}
              {post.image_url && (
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image_url} 
                    alt={post.title}
                    className="w-full h-full object-cover filter grayscale-[30%] contrast-125 group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  
                  {/* CRT vignette */}
                  <div className="absolute inset-0 shadow-[inset_0_0_100px_rgba(0,0,0,0.8)]" />
                  
                  {/* Corruption indicator */}
                  <div className="absolute top-2 right-2 font-mono text-xs">
                    <span className="text-green-500/60">CORRUPTION: </span>
                    <span className={getCorruptionStyle(post.corruption_level)}>
                      {post.corruption_level}%
                    </span>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="p-4">
                {/* Meta info */}
                <div className="flex items-center justify-between mb-3 text-xs font-mono">
                  <span className="text-green-500/40">{post.document_id}</span>
                  <span className="text-green-500/40">{formatDate(post.created_at)}</span>
                </div>

                {/* Title */}
                <h3 className="font-mono text-green-500 text-sm md:text-base mb-3 leading-tight group-hover:text-green-400 transition-colors">
                  {post.title}
                </h3>

                {/* Content preview */}
                <p className={`font-mono text-green-500/60 text-xs leading-relaxed ${expandedPost === post.id ? '' : 'line-clamp-4'}`}>
                  {post.content}
                </p>

                {/* Expand button */}
                <button
                  onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
                  className="mt-3 font-mono text-xs text-green-500/50 hover:text-green-500 transition-colors"
                >
                  [{expandedPost === post.id ? 'COLLAPSE' : 'EXPAND TRANSMISSION'}]
                </button>

                {/* Footer */}
                <div className="mt-4 pt-3 border-t border-green-500/10 flex items-center justify-between">
                  <span className="font-mono text-xs text-green-500/40">
                    SOURCE: {PERSONALITY_LABELS[post.personality] || post.personality.toUpperCase()}
                  </span>
                  <div className="flex gap-2">
                    {post.tags.slice(0, 2).map((tag) => (
                      <span 
                        key={tag}
                        className="px-2 py-0.5 bg-green-500/10 text-green-500/50 font-mono text-[10px] uppercase"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Glitch effect on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300">
                <div className="absolute inset-0 bg-green-500/5 animate-pulse" />
              </div>
            </article>
          ))}
        </div>

        {/* Footer text */}
        <div className="text-center mt-12 space-y-4">
          {posts.length > 0 && (
            <Link 
              to="/transmissions"
              className="inline-block px-6 py-3 border border-green-500/30 bg-green-500/5 text-green-500 font-mono text-sm hover:bg-green-500/10 hover:border-green-500/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,255,0,0.2)]"
            >
              [ACCESS FULL ARCHIVE]
            </Link>
          )}
          <p className="text-green-500/30 font-mono text-xs">
            [END OF INTERCEPTED BROADCAST - SIGNAL INTEGRITY: {Math.floor(Math.random() * 30 + 70)}%]
          </p>
        </div>
      </div>
    </section>
  );
}
